import { useState, useEffect } from "react"
import type { MapboxGeoJSONFeature } from "mapbox-gl"
import type { IParcel } from "@/types/explore"
import type { PluInfoType } from "@/types/explore"
import type { POIMarker, MutationMarker } from "@/types/explore"
import { geoFetch } from "@/lib/explore/api/geo-fetch"
import { formatNumber } from "@/lib/explore/utils/format-helpers"
import { Button } from "@/components/ui/button"
import { ChevronUpIcon, ChevronDownIcon, XIcon } from "lucide-react"
import { CopyButton } from "./copy-button"
import { ExternalLinks } from "./external-links"
import { KeyIndicators } from "./key-indicators"
import { ParcelCardDetails } from "./parcel-card-details"

interface MultiParcelItemDetailsProps {
  parcel: IParcel
  selectedPLU: MapboxGeoJSONFeature | null
  setParcelPOI: (poi: { lon: number | null; lat: number | null }) => void
  onPLULoaded: (plu: PluInfoType | null) => void
  onPoiFlyTo?: (center: [number, number]) => void
  onActivePOIsChange?: (pois: POIMarker[]) => void
  onHoverPOI?: (poi: POIMarker | null) => void
  addRecentParcel?: (parcel: IParcel) => void
  onActiveMutationsChange?: (markers: MutationMarker[]) => void
}

const MultiParcelItemDetails = ({
  parcel,
  selectedPLU,
  setParcelPOI,
  onPLULoaded,
  onPoiFlyTo,
  onActivePOIsChange,
  onHoverPOI,
  onActiveMutationsChange,
}: MultiParcelItemDetailsProps) => {
  const [effectivePLU, setEffectivePLU] = useState<PluInfoType | null>(null)
  const [pluLoading, setPluLoading] = useState(false)

  const center = parcel?.center ?? null
  useEffect(() => {
    if (!center) {
      setEffectivePLU(null)
      return
    }
    const [lon, lat] = center
    setPluLoading(true)
    const url = `https://www.geoportail-urbanisme.gouv.fr/api/feature-info/du?lon=${lon}&lat=${lat}&type=du&typeName=zone_urba`
    geoFetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`GPU API error: ${res.status}`)
        return res.json()
      })
      .then((json) => {
        const features = json?.features || json
        const feature = Array.isArray(features) ? features[0] : features
        const props = feature?.properties || feature
        if (props?.libelle) {
          const plu: PluInfoType = {
            libelle: props.libelle,
            libelong: props.libelong,
            typezone: props.typezone,
            gpu_timestamp: props.datvalid,
            gpu_doc_id: props.gpu_doc_id,
            nomfic: props.nomfic,
            partition: props.partition,
          }
          setEffectivePLU(plu)
          onPLULoaded(plu)
        } else {
          setEffectivePLU(null)
          onPLULoaded(null)
        }
      })
      .catch(() => {
        setEffectivePLU(null)
        onPLULoaded(null)
      })
      .finally(() => setPluLoading(false))
  }, [center?.[0], center?.[1]])

  return (
    <div className="mx-2 mb-2 mt-1 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden anim-fade-in">
      {/* Address */}
      <div className="px-2.5 py-1.5 bg-gray-50 border-b border-gray-100">
        <div className="text-sm text-gray-500 flex items-center gap-1">
          {parcel.fullAddress || parcel.address || ""}
          {(parcel.fullAddress || parcel.address) && (
            <CopyButton text={parcel.fullAddress || parcel.address || ""} title="Copier l'adresse complete" />
          )}
        </div>
      </div>

      {/* Key indicators */}
      <div className="px-2 pb-1 pt-1">
        <KeyIndicators selectedParcel={parcel} elevation={null} ownerType={null} />
      </div>

      {/* Details */}
      <ParcelCardDetails
        selectedParcel={parcel}
        selectedPLU={selectedPLU}
        setParcelPOI={setParcelPOI}
        onPLULoaded={onPLULoaded}
        onFlyTo={onPoiFlyTo}
        onActivePOIsChange={onActivePOIsChange}
        onHoverPOI={onHoverPOI}
        onActiveMutationsChange={onActiveMutationsChange}
        effectivePLU={effectivePLU}
        pluLoading={pluLoading}
        buildingData={parcel.buildings ?? null}
      />

      {/* External links */}
      <div className="px-2.5 py-1.5 border-t border-gray-100">
        <ExternalLinks parcel={parcel} compact />
      </div>
    </div>
  )
}

interface MultiParcelViewProps {
  selectedParcels: IParcel[]
  setSelectedParcels: React.Dispatch<React.SetStateAction<IParcel[]>>
  openParcelIndices: Set<number>
  toggleParcel: (index: number) => void
  toggleAll: () => void
  selectedPLU: MapboxGeoJSONFeature | null
  onClose: () => void
  onCreateFolder: () => void
  setParcelPOI: (poi: { lon: number | null; lat: number | null }) => void
  onPLULoaded: (plu: PluInfoType | null) => void
  onPoiFlyTo?: (center: [number, number]) => void
  onActivePOIsChange?: (pois: POIMarker[]) => void
  onHoverPOI?: (poi: POIMarker | null) => void
  addRecentParcel?: (parcel: IParcel) => void
  onActiveMutationsChange?: (markers: MutationMarker[]) => void
}

export const MultiParcelView = ({
  selectedParcels,
  setSelectedParcels,
  openParcelIndices,
  toggleParcel,
  toggleAll,
  selectedPLU,
  onClose,
  onCreateFolder,
  setParcelPOI,
  onPLULoaded,
  onPoiFlyTo,
  onActivePOIsChange,
  onHoverPOI,
  addRecentParcel,
  onActiveMutationsChange,
}: MultiParcelViewProps) => {
  const allOpen = openParcelIndices.size === selectedParcels.length

  return (
    <>
      <div className="border-t border-gray-200" />
      {/* Summary header */}
      <div className="px-3 py-2 shrink-0 flex items-center gap-2.5 anim-fade-in">
        <div className="w-7 h-7 rounded-lg bg-primary text-white font-bold text-sm flex items-center justify-center shrink-0">
          {selectedParcels.length}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-gray-800">{selectedParcels.length} parcelles</div>
          <div className="text-xs text-gray-400">
            {formatNumber(
              Math.round(selectedParcels.reduce((t: number, p) => t + (p?.properties?.contenance ?? 0), 0)),
            )}{" "}
            m{"\u00B2"} au total
          </div>
        </div>
        <Button
          onClick={toggleAll}
          variant="ghost"
          size="icon-sm"
          className="text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          title={allOpen ? "Tout replier" : "Tout deplier"}
        >
          {allOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </Button>
        <Button
          onClick={onClose}
          variant="ghost"
          size="icon-sm"
          className="text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          title="Tout deselectionner"
        >
          <XIcon />
        </Button>
      </div>

      {/* Scrollable parcel list + details */}
      <div className="flex-1 overflow-y-auto scrollbar-thin" style={{ scrollbarGutter: "stable" }}>
        {selectedParcels.map((parcel, i) => {
          const isOpen = openParcelIndices.has(i)
          return (
            <div key={parcel.properties?.idu || parcel.id || i}>
              {/* Compact row */}
              <button
                onClick={() => toggleParcel(i)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors border-t border-gray-100 border-l-[3px] ${
                  isOpen ? "bg-primary-lighter border-l-primary" : "hover:bg-gray-50 border-l-transparent"
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 text-xs font-bold transition-colors ${
                    isOpen ? "bg-primary text-white" : "bg-primary/[0.08] text-primary"
                  }`}
                >
                  {parcel.properties?.section}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-gray-800">
                    {parcel.properties?.section} {parcel.properties?.numero}
                  </div>
                  <div className="text-xs text-gray-400">
                    {formatNumber(Math.round(parcel.properties?.contenance ?? 0))} m{"\u00B2"}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedParcels(selectedParcels.filter((_, idx) => idx !== i))
                  }}
                  className="cursor-pointer p-1.5 rounded-md text-gray-300 hover:bg-red-50 hover:text-red-500 transition-colors"
                  title="Retirer"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </button>

              {/* Expanded details */}
              {isOpen && (
                <MultiParcelItemDetails
                  parcel={parcel}
                  selectedPLU={selectedPLU}
                  setParcelPOI={setParcelPOI}
                  onPLULoaded={onPLULoaded}
                  onPoiFlyTo={onPoiFlyTo}
                  onActivePOIsChange={onActivePOIsChange}
                  onHoverPOI={onHoverPOI}
                  addRecentParcel={addRecentParcel}
                  onActiveMutationsChange={onActiveMutationsChange}
                />
              )}
            </div>
          )
        })}
      </div>

      {/* Sticky CTA footer */}
      <div className="px-3 py-2.5 border-t border-gray-200 bg-white shrink-0 rounded-b-lg">
        <button
          onClick={onCreateFolder}
          className="w-full cursor-pointer bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary-dark transition-all shadow-sm hover:shadow-md btn-press h-11"
        >
          Creer un dossier Kaptcher
        </button>
      </div>
    </>
  )
}
